import { useGuidePlayer } from '@/hooks/use-guide-player';
import { useProvidedProgramContext } from '@/hooks/use-provided-program-context';
import useToastDialog from '@/hooks/use-toast-dialog';
import { useUpdateGuide } from '@/hooks/use-update-guide';
import { ThumbsDownFilled } from '@/libs/material/icons/thumbs-down-filled';
import { ThumbsDownOutline } from '@/libs/material/icons/thumbs-down-outline';
import { ThumbsUpFilled } from '@/libs/material/icons/thumbs-up-filled';
import { ThumbsUpOutline } from '@/libs/material/icons/thumbs-up-outline';
import { DS_COLOR, DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { MediaGuide, MediaProgram } from '@calm/evexia-web-sdk';
import commonMessages from '@rhg/lang/messages/common';
import programMessages from '@rhg/lang/messages/program';
import {
    Box,
    IconButton,
    Theme,
    Typography,
    useTheme,
} from '@rhg/libs/material';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

const styleRatingButtonMin = {
    borderRadius: '50%',
    p: 3,
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? 'background.paper'
            : theme.palette.mask.black['60p'],
    color: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? DS_COLOR.LIGHT_PRIMARY_MAIN
            : DS_COLOR.LIGHT_PRIMARY_TEXT_CONTRAST,
};
const styleToastDialogIconContainer = {
    width: 64,
    height: 64,
    p: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'dark'
            ? DS_COLOR.LIGHT_SECONDARY_MAIN
            : DS_COLOR.LIGHT_SECONDARY_LIGHT,
};
const styleToastDialogTitleWrapper = { mb: 0 };
const styleToastDialogTitle = { mb: 0 };
const styleToastDialogContentContainer = {
    px: 6,
    py: 4,
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'dark'
            ? DS_COLOR.DARK_BACKGROUND_PAPER
            : 'background.paper',
};
const styleRatingContainerOptions = {
    display: 'flex',
    justifyContent: 'center',
    gap: DS_SPACING_UNITS.SIDE_SMALL,
};
const styleRatingWrapper = {
    zIndex: 2,
};
const styleToastIcon = {
    height: 40,
    width: 40,
    color: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? DS_COLOR.LIGHT_SECONDARY_MAIN
            : DS_COLOR.DARK_SECONDARY_LIGHT,
};
const getProgramGuide = (
    program?: MediaProgram,
    guideId?: string
): MediaGuide | undefined => {
    if (!program || !guideId) {
        return undefined;
    }
    return program.guides.find((guide) => guide.id === guideId);
};

const SessionRating = () => {
    const { formatMessage } = useIntl();
    const [hasRatedSession, setHasRatedSession] = useState(false);
    const { playingGuide: guide } = useGuidePlayer();
    const { updateGuide } = useUpdateGuide();
    const { program, packClass } = useProvidedProgramContext();
    const { toast } = useToastDialog();
    const theme = useTheme();

    const guideRating = useMemo(() => {
        const programGuide = getProgramGuide(program, guide?.id);
        return programGuide?.rating ?? guide?.rating ?? undefined;
    }, [guide, program]);

    const rateSession = useCallback(
        (rating: number) => () => {
            const isPositiveRating = rating > 0;
            updateGuide(
                {
                    guideId: guide?.id,
                    packClass,
                    programId: program!.id,
                    request: {
                        rating,
                    },
                },
                {
                    onSuccess: () => {
                        setHasRatedSession(true);
                        toast({
                            title: formatMessage(
                                programMessages['program.your_answer_submitted']
                            ),
                            icon: isPositiveRating ? (
                                <ThumbsUpFilled sx={styleToastIcon} />
                            ) : (
                                <ThumbsDownFilled sx={styleToastIcon} />
                            ),
                            SimpleDialogProps: {
                                titleVariant: 'h6',
                                titleColor:
                                    theme.palette.mode === 'dark'
                                        ? DS_COLOR.DARK_TEXT_BODY
                                        : 'text.body',
                                dialogMaxWidth: 343,
                                iconContainerStyle:
                                    styleToastDialogIconContainer,
                                contentContainerStyle:
                                    styleToastDialogContentContainer,
                                titleWrapperStyle: styleToastDialogTitleWrapper,
                                titleStyle: styleToastDialogTitle,
                            },
                        });
                    },
                }
            );
        },
        [
            formatMessage,
            guide?.id,
            packClass,
            program,
            theme.palette.mode,
            toast,
            updateGuide,
        ]
    );

    const needsRating = useMemo(
        () => guideRating === undefined || guideRating === null,
        [guideRating]
    );

    if (hasRatedSession) {
        return null;
    }

    return (
        <Box sx={styleRatingWrapper}>
            {needsRating && !hasRatedSession && (
                <Box>
                    <Typography
                        variant="subtitle1"
                        component="p"
                        color="text.sub"
                        textAlign="center"
                        mb={DS_SPACING_UNITS.CONTENT}
                    >
                        {formatMessage(
                            programMessages[
                                'program.did_you_find_session_helpful'
                            ]
                        )}
                    </Typography>
                    <Box sx={styleRatingContainerOptions}>
                        <IconButton
                            trackButton="SessionRatingYes"
                            onClick={rateSession(10)}
                            sx={styleRatingButtonMin}
                            aria-label={formatMessage(
                                commonMessages['common.yes']
                            )}
                        >
                            <ThumbsUpOutline />
                        </IconButton>
                        <IconButton
                            trackButton="SessionRatingNo"
                            onClick={rateSession(0)}
                            sx={styleRatingButtonMin}
                            aria-label={formatMessage(
                                commonMessages['common.no']
                            )}
                        >
                            <ThumbsDownOutline />
                        </IconButton>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default SessionRating;
